import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDateHeure } from "@/lib/format";
import { Vide } from "@/components/commun";

const AUTEURS = { humain: "Humain", ia: "IA", systeme: "Système" };
const ACTIONS = { creation: "Création", modification: "Modification", suppression: "Suppression", restauration: "Restauration", suppression_definitive: "Suppression définitive", purge_automatique: "Purge automatique" };
const FILTRES = [["", "Tous"], ["humain", "Humain"], ["ia", "IA"], ["systeme", "Système"]];

const CHAMPS = {
  deleted_at: "Date de suppression", actif: "Compte actif", statut: "Statut", role: "Profil", role_label: "Profil (libellé)",
  prenom: "Prénom", nom: "Nom", email: "E-mail", partenaire_id: "Société partenaire", cle_chiffree: "Clé d'API",
  theme_actif: "Thème actif", etape: "Étape", montant_estime: "Montant estimé", commission: "Commission",
  ville: "Ville", telephone: "Téléphone", invitation_expire: "Expiration de l'invitation", derniere_connexion: "Dernière connexion",
  created_at: "Date de création", id: "Identifiant", invite_par: "Invité par",
  operation: "Opération", processus_id: "Processus", motif_perte: "Motif de perte", champs: "Champs personnalisés", tags: "Tags",
  bien_id: "Bien", agent_id: "Agent", confirmateur_id: "Confirmateur", source: "Source", telephones: "Téléphones",
  adresse: "Adresse", code_postal: "Code postal", type: "Type", civilite: "Civilité", affichage: "Nom affiché",
  organisation_id: "Organisation", departement: "Département", fait: "Fait", etapes: "Étapes", archive: "Archivé",
  assigne_a: "Assigné à", cree_par: "Créé par", echeance: "Échéance", fiche_id: "Fiche", contact_id: "Contact", note: "Note",
  fait_le: "Fait le", libelle: "Libellé", cle: "Clé", cible: "Cible", options: "Options", couleur: "Couleur",
};
const VALEURS = { en_cours: "En cours", gagne: "Gagné", perdu: "Perdu", particulier: "Particulier", collectivite: "Collectivité",
  proprietaire_bailleur: "Propriétaire bailleur", entreprise: "Entreprise", agriculteur: "Agriculteur", actif: "Actif", pause: "En pause", brouillon: "Brouillon", invite: "Invité" };
const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const JOUR = /^\d{4}-\d{2}-\d{2}$/;
const cleLisible = (k) => { const s = k.replace(/_/g, " "); return s.charAt(0).toUpperCase() + s.slice(1); };
const texte = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Oui" : "Non";
  if (typeof v === "string" && ISO.test(v)) return formatDateHeure(v);
  if (typeof v === "string" && JOUR.test(v)) return `${v.slice(8, 10)}/${v.slice(5, 7)}/${v.slice(0, 4)}`;
  if (typeof v === "string" && VALEURS[v]) return VALEURS[v];
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "object") return Object.keys(v).length ? Object.entries(v).map(([k, x]) => `${cleLisible(k)} : ${texte(x)}`).join(" · ") : "—";
  return typeof v === "object" ? JSON.stringify(v) : String(v);
};

export const JournalEntree = ({ entree }) => (
  <div className="at-fiche p-4 mb-2" data-testid={`journal-entry-${entree.id}`}>
    <div className="flex flex-wrap items-center gap-2">
      <span className="px-2.5 py-0.5 at-pill text-xs font-bold" style={{ background: entree.auteur_type === "ia" ? "var(--glow)" : "var(--surface-2)" }} data-testid="journal-author-type">{AUTEURS[entree.auteur_type]}</span>
      <span className="font-semibold text-sm">{entree.auteur_nom}</span>
      <span className="text-xs at-muted ml-auto">{formatDateHeure(entree.date)}</span>
    </div>
    <p className="text-sm mt-2"><span className="font-semibold">{ACTIONS[entree.action] || entree.action}</span> · {entree.libelle}</p>
    {entree.changements?.length > 0 && (
      <ul className="mt-2 space-y-1">
        {entree.changements.slice(0, 8).map((c) => (
          <li key={c.champ} className="text-xs at-soft px-3 py-2 break-words"><span className="font-semibold">{CHAMPS[c.champ] || c.champ}</span> : <span className="at-muted line-through">{texte(c.ancienne)}</span> → {texte(c.nouvelle)}</li>
        ))}
      </ul>
    )}
  </div>
);

export default function Journal() {
  const [filtre, setFiltre] = useState("");
  const [liste, setListe] = useState([]);
  useEffect(() => { api.get("/journal", { params: { auteur_type: filtre || undefined } }).then((r) => setListe(r.data)); }, [filtre]);
  return (
    <div>
      <h2 className="text-base md:text-lg font-bold mb-3">Historique des modifications</h2>
      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
        {FILTRES.map(([v, l]) => (
          <button key={v} type="button" onClick={() => setFiltre(v)} data-testid={`journal-filter-${v || "tous"}`} className="min-h-[44px] px-4 at-pill font-semibold text-sm whitespace-nowrap"
            style={{ background: filtre === v ? "var(--accent)" : "var(--surface)", color: filtre === v ? "var(--accent-ink)" : "var(--ink)" }}>{l}</button>
        ))}
      </div>
      {liste.length === 0 && <Vide texte="Aucune entrée." testId="journal-empty" />}
      <div data-testid="journal-list">{liste.map((e) => <JournalEntree key={e.id} entree={e} />)}</div>
    </div>
  );
}
